(function () {
  const PREFERENCE_KEY = 'mnze-cart-shipping-protection-enabled';
  const DEFAULT_VARIANT_ID = '52815593013514';
  const API_SECTION_ID = 'api-cart-items';
  const ERROR_MESSAGE = 'Die Versandversicherung konnte nicht aktualisiert werden. Bitte versuche es erneut.';

  if (window.__cartDrawerShippingProtectionInit) return;
  window.__cartDrawerShippingProtectionInit = true;

  class CartDrawerShippingProtection {
    constructor() {
      this.awaitingFallbackRefresh = false;
      this.chatVisibilityObserver = null;
      this.fallbackSignalResetTimer = 0;
      this.isProcessing = false;
      this.ignoringFallbackSignals = false;
      this.isThemeCartBuildWrapped = false;
      this.isFlushingLineItemUpdates = false;
      this.pendingJob = null;
      this.lineItemUpdateFlushPromise = null;
      this.queuedLineItemUpdates = new Map();
      this.memoryPreference = true;
      this.state = {
        variantId: DEFAULT_VARIANT_ID,
        loading: false,
        errorMessage: '',
        lastConfirmedPreference: this.getStoredPreference(),
      };

      this.handleToggleChange = this.handleToggleChange.bind(this);
      this.handleCartSignal = this.handleCartSignal.bind(this);
      this.handleDrawerRemoveClick = this.handleDrawerRemoveClick.bind(this);
      this.handleDrawerQuantityButtonClick = this.handleDrawerQuantityButtonClick.bind(this);
      this.handleDrawerQuantityFieldChange = this.handleDrawerQuantityFieldChange.bind(this);
      this.handleDrawerQuantityUpdate = this.handleDrawerQuantityUpdate.bind(this);
      this.syncChatVisibility = this.syncChatVisibility.bind(this);
    }

    init() {
      if (!this.getDrawer()) return;

      this.wrapThemeCartBuild();
      this.syncThemeCartReferences();
      this.setupChatVisibilitySync();
      this.prehydrateStoredPreference();

      document.addEventListener('change', this.handleToggleChange);
      document.addEventListener('click', this.handleDrawerRemoveClick, true);
      document.addEventListener('click', this.handleDrawerQuantityButtonClick, true);
      document.addEventListener('change', this.handleDrawerQuantityFieldChange, true);
      document.addEventListener('theme:cart:update', this.handleDrawerQuantityUpdate, true);
      document.addEventListener('theme:cart:change', this.handleCartSignal);
      document.addEventListener('theme:cart:open', this.handleCartSignal);
      document.addEventListener('theme:product:added', this.handleCartSignal);

      this.scheduleReconcile({
        reason: 'init',
        refreshSummary: true,
        modePriority: 0,
        respectStoredPreference: true,
      });
    }

    getRoute(key, fallback) {
      if (window.routes && window.routes[key]) return window.routes[key];
      if (window.theme && window.theme.routes && window.theme.routes[key]) return window.theme.routes[key];
      return fallback;
    }

    getDrawer() {
      return document.querySelector('[data-cart-drawer]');
    }

    isDrawerOpen() {
      return Boolean(this.getDrawer()?.classList.contains('is-open'));
    }

    syncChatVisibility() {
      document.body.classList.toggle('mnze-cart-drawer-open', this.isDrawerOpen());
    }

    setupChatVisibilitySync() {
      const drawer = this.getDrawer();

      if (!drawer || this.chatVisibilityObserver) {
        this.syncChatVisibility();
        return;
      }

      this.chatVisibilityObserver = new MutationObserver(() => {
        this.syncChatVisibility();
      });

      this.chatVisibilityObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
      });

      document.addEventListener('theme:cart:open', this.syncChatVisibility);
      document.addEventListener('theme:cart:change', this.syncChatVisibility);
      window.addEventListener('pageshow', this.syncChatVisibility);

      this.syncChatVisibility();
    }

    getSummaryHolder() {
      return this.getDrawer()?.querySelector('[data-cart-summary-holder]') || null;
    }

    getProtectionBlock() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection]') || null;
    }

    getToggle() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection-toggle]') || null;
    }

    getErrorNode() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection-error]') || null;
    }

    getGiftPromo() {
      return this.getDrawer()?.querySelector('[data-cart-gift-promo]') || null;
    }

    hideGiftPromo() {
      const promo = this.getGiftPromo();

      if (promo) {
        promo.hidden = true;
      }
    }

    getUpsellWidget() {
      return this.getDrawer()?.querySelector('[data-upsell-widget]') || null;
    }

    getUpsellHolder() {
      return this.getDrawer()?.querySelector('[data-upsell-products]') || null;
    }

    getSummaryGiftEligibleQuantity(summaryNode = this.getSummaryHolder()) {
      const marker = summaryNode?.querySelector?.('[data-cart-gift-eligible-quantity]');
      const quantity = Number(marker?.textContent || '');

      return Number.isFinite(quantity) && quantity >= 0 ? quantity : null;
    }

    getPromoGiftEligibleQuantity(promoNode = this.getGiftPromo()) {
      const quantity = Number(promoNode?.dataset?.cartGiftEligibleQuantity || '');

      return Number.isFinite(quantity) && quantity >= 0 ? quantity : null;
    }

    getLineItemGiftEligibleQuantity() {
      const drawer = this.getDrawer();
      let eligibleQuantity = 0;

      if (!drawer) {
        return null;
      }

      drawer.querySelectorAll('[data-item][data-cart-gift-eligible="true"]').forEach((itemNode) => {
        const quantityField = this.getDrawerQuantityField(itemNode);
        const quantity = Number(quantityField?.value || itemNode.dataset.itemQuantity || 0);

        if (Number.isFinite(quantity) && quantity > 0) {
          eligibleQuantity += quantity;
        }
      });

      return eligibleQuantity;
    }

    getGiftEligibleQuantity(options = {}) {
      if (Number.isFinite(options.eligibleQuantityCountOverride)) {
        return Number(options.eligibleQuantityCountOverride);
      }

      const summaryQuantity = this.getSummaryGiftEligibleQuantity(options.summaryNode);
      if (Number.isFinite(summaryQuantity)) {
        return summaryQuantity;
      }

      const promoQuantity = this.getPromoGiftEligibleQuantity(options.promoNode);
      if (Number.isFinite(promoQuantity)) {
        return promoQuantity;
      }

      if (options.fallbackToLineItems === false) {
        return null;
      }

      return this.getLineItemGiftEligibleQuantity();
    }

    getDrawerItem(node) {
      const drawer = this.getDrawer();
      const item = node?.closest?.('[data-item]') || null;

      if (!drawer || !item || !drawer.contains(item)) {
        return null;
      }

      return item;
    }

    findDrawerItemByKey(itemKey) {
      if (!itemKey) {
        return null;
      }

      const drawer = this.getDrawer();
      if (!drawer) {
        return null;
      }

      for (const item of drawer.querySelectorAll('[data-item]')) {
        if (item.dataset.item === itemKey) {
          return item;
        }
      }

      return null;
    }

    getDrawerQuantityField(item) {
      return item?.querySelector?.('.cart__quantity-field') || null;
    }

    isDisabledControl(node) {
      return Boolean(
        node?.hasAttribute?.('disabled')
        || node?.getAttribute?.('aria-disabled') === 'true'
        || node?.classList?.contains('is-disabled')
      );
    }

    getCartChangeJsonUrl() {
      const route = this.getRoute('cart_change_url', '/cart/change');
      return route.endsWith('.js') ? route : `${route}.js`;
    }

    syncDrawerLineIndexes(cart) {
      const drawer = this.getDrawer();

      if (!drawer || !cart || !Array.isArray(cart.items)) {
        return;
      }

      const lineIndexByKey = new Map();

      cart.items.forEach((item, index) => {
        if (item?.key) {
          lineIndexByKey.set(String(item.key), String(index + 1));
        }
      });

      drawer.querySelectorAll('[data-item]').forEach((itemNode) => {
        const itemKey = itemNode.dataset.item || '';
        const lineIndex = lineIndexByKey.get(itemKey);

        if (lineIndex) {
          itemNode.dataset.itemIndex = lineIndex;
          const removeLink = itemNode.querySelector('[data-item-remove]');

          if (removeLink) {
            removeLink.href = `${this.getRoute('cart_change_url', '/cart/change')}?line=${lineIndex}&quantity=0`;
          }
        }
      });
    }

    async updateDrawerLineItem(detail, item) {
      const quantity = Number(detail?.quantity);
      const itemKey = item?.dataset?.item || detail?.id || '';

      if (!itemKey || Number.isNaN(quantity)) {
        return;
      }

      const liveItem = item || this.findDrawerItemByKey(itemKey);

      if (liveItem) {
        liveItem.classList.toggle('is-loading', quantity > 0);
        liveItem.classList.toggle('is-removed', quantity <= 0);
      }

      try {
        const response = await fetch(this.getCartChangeJsonUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: itemKey, quantity }),
        });

        if (!response.ok) {
          throw new Error('Cart line update failed');
        }

        const body = await response.json();

        if (body?.errors) {
          throw new Error('Cart line update failed');
        }

        if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
        }
      } catch (error) {
        const errorItem = liveItem || this.findDrawerItemByKey(itemKey);

        if (errorItem) {
          errorItem.classList.remove('is-loading', 'is-removed');
        }

        throw error;
      }
    }

    queueDrawerLineItemUpdate(detail, item) {
      const itemKey = String(detail?.id || item?.dataset?.item || '');
      const quantity = Number(detail?.quantity);

      if (!itemKey || Number.isNaN(quantity)) {
        return Promise.resolve();
      }

      const liveItem = this.findDrawerItemByKey(itemKey) || item || null;
      const quantityField = this.getDrawerQuantityField(liveItem);

      if (quantityField) {
        quantityField.value = String(quantity);
      }

      if (liveItem) {
        liveItem.dataset.itemQuantity = String(quantity);
      }

      this.queuedLineItemUpdates.set(itemKey, {
        quantity,
        item: liveItem,
        retryCount: 0,
      });

      return this.startQueuedLineItemFlush();
    }

    startQueuedLineItemFlush() {
      if (this.isFlushingLineItemUpdates) {
        return this.lineItemUpdateFlushPromise || Promise.resolve();
      }

      let shouldResume = false;
      this.isFlushingLineItemUpdates = true;
      this.lineItemUpdateFlushPromise = (async () => {
        try {
          while (this.queuedLineItemUpdates.size > 0) {
            const pendingUpdates = Array.from(this.queuedLineItemUpdates.entries());
            this.queuedLineItemUpdates.clear();

            for (let index = 0; index < pendingUpdates.length; index += 1) {
              const [pendingItemKey, pendingUpdate] = pendingUpdates[index];
              const pendingItem = this.findDrawerItemByKey(pendingItemKey) || pendingUpdate.item || null;

              try {
                await this.updateDrawerLineItem(
                  {
                    id: pendingItemKey,
                    quantity: pendingUpdate.quantity,
                  },
                  pendingItem
                );
              } catch (error) {
                if (pendingUpdate.retryCount < 1 && !this.queuedLineItemUpdates.has(pendingItemKey)) {
                  this.queuedLineItemUpdates.set(pendingItemKey, {
                    ...pendingUpdate,
                    item: this.findDrawerItemByKey(pendingItemKey) || pendingItem,
                    retryCount: pendingUpdate.retryCount + 1,
                  });
                }

                for (const [remainingItemKey, remainingUpdate] of pendingUpdates.slice(index + 1)) {
                  if (!this.queuedLineItemUpdates.has(remainingItemKey)) {
                    this.queuedLineItemUpdates.set(remainingItemKey, remainingUpdate);
                  }
                }

                shouldResume = this.queuedLineItemUpdates.size > 0;
                throw error;
              }
            }
          }
        } finally {
          this.isFlushingLineItemUpdates = false;
          this.lineItemUpdateFlushPromise = null;

          if (shouldResume || this.queuedLineItemUpdates.size > 0) {
            window.queueMicrotask(() => {
              if (!this.isFlushingLineItemUpdates && this.queuedLineItemUpdates.size > 0) {
                this.startQueuedLineItemFlush();
              }
            });
          }
        }
      })();

      return this.lineItemUpdateFlushPromise;
    }

    renderGiftPromo(cartState) {
      const promo = this.getGiftPromo();
      if (!promo) return;

      const unlockQuantity = Number(promo.dataset.cartGiftUnlockQuantity || 2);
      const cycleSize = unlockQuantity + 1;
      const eligibleQuantity = Number(cartState?.eligibleQuantityCount || 0);
      const cyclePosition = eligibleQuantity % cycleSize;
      const completedFreePaintings = Math.floor(eligibleQuantity / cycleSize);
      const hasEarnedFree = completedFreePaintings > 0;
      const isUnlocked = eligibleQuantity > 0 && cyclePosition === unlockQuantity;
      const remainingQuantity = isUnlocked
        ? 0
        : cyclePosition === 0 && eligibleQuantity > 0
          ? unlockQuantity
          : Math.max(unlockQuantity - cyclePosition, 0);
      const message = promo.querySelector('[data-cart-gift-message]');
      const progress = promo.querySelector('[data-cart-gift-progress]');

      promo.dataset.cartGiftEligibleQuantity = String(eligibleQuantity);
      promo.classList.toggle('is-success', isUnlocked);
      promo.classList.toggle('has-step-one', eligibleQuantity >= 1);
      promo.classList.toggle('has-earned-free', hasEarnedFree);
      promo.toggleAttribute('hidden', !cartState?.hasVisibleItems);

      if (message) {
        if (isUnlocked && completedFreePaintings >= 1) {
          message.textContent = '🎉 Geschafft! Du bekommst noch 1 Bild gratis. Wähle dein Gratisbild.';
        } else if (isUnlocked) {
          message.textContent = '🎉 Geschafft! Das dritte ist gratis. Wähle dein Gratisbild.';
        } else if (hasEarnedFree && completedFreePaintings === 1 && remainingQuantity === 2) {
          message.textContent = '🎉 Du bekommst 1 Bild gratis. Füge noch 2 hinzu für ein weiteres.';
        } else if (hasEarnedFree && completedFreePaintings === 1 && remainingQuantity === 1) {
          message.textContent = '🎉 Du bekommst 1 Bild gratis. Füge noch 1 Bild hinzu für ein weiteres.';
        } else if (hasEarnedFree) {
          message.textContent = `🎉 Du bekommst ${completedFreePaintings} Bilder gratis. Füge noch ${remainingQuantity} hinzu für ein weiteres Gratisbild.`;
        } else if (remainingQuantity === 1) {
          message.textContent = '🔥 Noch 1 Bild – das dritte ist gratis.';
        } else {
          message.textContent = '🔥 Noch 2 Bilder – das dritte ist gratis.';
        }
      }

      if (progress) {
        const value = Math.max(0, Math.min(100, (cyclePosition / unlockQuantity) * 100));
        progress.value = value;
        progress.setAttribute('value', String(value));
      }
    }

    getStoredPreference() {
      try {
        return window.sessionStorage.getItem(PREFERENCE_KEY) !== 'false';
      } catch (error) {
        return this.memoryPreference;
      }
    }

    setStoredPreference(enabled) {
      this.memoryPreference = Boolean(enabled);

      try {
        if (enabled) {
          window.sessionStorage.removeItem(PREFERENCE_KEY);
        } else {
          window.sessionStorage.setItem(PREFERENCE_KEY, 'false');
        }
      } catch (error) {
        // Ignore storage errors in private browsing modes
      }
    }

    getCartJsonUrl() {
      return `${this.getRoute('cart_url', '/cart').replace(/\/$/, '')}.js`;
    }

    getSectionUrl() {
      return `${this.getRoute('cart_url', '/cart')}?section_id=${API_SECTION_ID}`;
    }

    async fetchCart() {
      const response = await fetch(this.getCartJsonUrl(), {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Cart fetch failed');
      }

      return response.json();
    }

    async fetchSummaryMarkup() {
      return this.fetchSectionFragment('[data-api-cart-summary]');
    }

    async fetchUpsellMarkup() {
      return this.fetchSectionFragment('[data-api-upsell-items]');
    }

    async fetchPriceMarkup() {
      return this.fetchSectionFragment('[data-api-cart-price]');
    }

    async fetchSectionDocument() {
      const response = await fetch(this.getSectionUrl(), {
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error('Cart summary fetch failed');
      }

      const html = await response.text();
      return new window.DOMParser().parseFromString(html, 'text/html');
    }

    async fetchSectionFragments(selectors) {
      const doc = await this.fetchSectionDocument();
      return selectors.map((selector) => doc.querySelector(selector));
    }

    async fetchSectionFragment(selector) {
      const [fragment] = await this.fetchSectionFragments([selector]);
      return fragment;
    }

    deriveCartState(cart, options = {}) {
      let insuranceQuantity = 0;
      let visibleQuantityCount = 0;
      let eligibleQuantityCount = this.getGiftEligibleQuantity(options);
      let hasVisibleItems = false;
      const respectStoredPreference = options.respectStoredPreference !== false;

      if (!Number.isFinite(eligibleQuantityCount)) {
        eligibleQuantityCount = 0;
      }

      (cart.items || []).forEach((item) => {
        const itemVariantId = String(item.variant_id || item.id || '');
        const quantity = Number(item.quantity || 0);

        if (itemVariantId === this.state.variantId) {
          insuranceQuantity += quantity;
          return;
        }

        hasVisibleItems = true;
        visibleQuantityCount += quantity;
      });

      if (!hasVisibleItems) {
        this.setStoredPreference(true);
        eligibleQuantityCount = 0;
      }

      const desiredEnabled = hasVisibleItems
        ? (respectStoredPreference ? this.getStoredPreference() : insuranceQuantity > 0)
        : false;
      const desiredQuantity = hasVisibleItems && desiredEnabled ? 1 : 0;

      return {
        cart,
        hasVisibleItems,
        visibleQuantityCount,
        eligibleQuantityCount,
        insuranceQuantity,
        inCart: insuranceQuantity > 0,
        desiredEnabled,
        desiredQuantity,
        totalPrice: Number(cart.total_price || 0),
      };
    }

    getStateSignature(cartState) {
      return JSON.stringify({
        totalPrice: cartState.totalPrice,
        insuranceQuantity: cartState.insuranceQuantity,
        visibleQuantityCount: cartState.visibleQuantityCount,
        eligibleQuantityCount: cartState.eligibleQuantityCount,
        hasVisibleItems: cartState.hasVisibleItems,
        items: (cartState.cart.items || []).map((item) => `${item.key || item.variant_id || item.id}:${item.quantity}`),
      });
    }

    hasStateDrift(referenceState, liveState) {
      return this.getStateSignature(referenceState) !== this.getStateSignature(liveState);
    }

    setLoading(isLoading) {
      this.state.loading = isLoading;
      this.syncLiveState();
    }

    setError(message) {
      this.state.errorMessage = message || '';

      const errorNode = this.getErrorNode();
      if (!errorNode) return;

      if (message) {
        errorNode.textContent = message;
        errorNode.classList.remove('is-hidden');
      } else {
        errorNode.textContent = '';
        errorNode.classList.add('is-hidden');
      }
    }

    syncLiveState() {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      if (toggle) {
        toggle.disabled = this.state.loading;
      }

      if (block) {
        block.classList.toggle('is-loading', this.state.loading);
      }

      this.setError(this.state.errorMessage);
    }

    prehydrateStoredPreference() {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      if (!block || !toggle) return;

      const hasVisibleItems = block.dataset.owaShippingProtectionHasVisibleItems === 'true';
      const inCart = block.dataset.owaShippingProtectionInCart === 'true';
      const desiredChecked = hasVisibleItems ? (inCart || this.getStoredPreference()) : false;

      toggle.checked = desiredChecked;
      block.toggleAttribute('hidden', !hasVisibleItems);
    }

    applyStateToDom(cartState, options = {}) {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      if (options.forceHideGiftPromo) {
        this.hideGiftPromo();
      } else if (!options.skipGiftPromo) {
        this.renderGiftPromo(cartState);
      }
      this.syncDrawerLineIndexes(cartState?.cart);

      if (block) {
        block.dataset.owaShippingProtectionVariantId = this.state.variantId;
        block.dataset.owaShippingProtectionInCart = cartState.inCart ? 'true' : 'false';
        block.dataset.owaShippingProtectionHasVisibleItems = cartState.hasVisibleItems ? 'true' : 'false';
        block.toggleAttribute('hidden', !cartState.hasVisibleItems);
        block.classList.toggle('is-loading', this.state.loading);
      }

      if (toggle) {
        toggle.checked = cartState.desiredEnabled;
        toggle.disabled = this.state.loading;
      }

      this.setError(this.state.errorMessage);
    }

    syncThemeCartReferences() {
      if (!window.cart) return;

      const drawer = this.getDrawer();
      window.cart.cartDrawer = drawer || window.cart.cartDrawer || null;
      window.cart.cart = drawer || window.cart.cart || null;
      window.cart.cartForm = drawer?.querySelector('[data-cart-form]') || window.cart.cartForm || null;
      window.cart.cartEmpty = drawer?.querySelector('[data-cart-empty]') || window.cart.cartEmpty || null;
      window.cart.buttonHolder = drawer?.querySelector('[data-foot-holder]') || window.cart.buttonHolder || null;
      window.cart.itemsHolder = drawer?.querySelector('[data-items-holder]') || window.cart.itemsHolder || null;
      window.cart.upsellProductsHolder = drawer?.querySelector('[data-upsell-products]') || window.cart.upsellProductsHolder || null;
      window.cart.priceHolder = drawer?.querySelector('[data-cart-price-holder]') || window.cart.priceHolder || null;
      window.cart.cartTotal = drawer?.querySelector('[data-cart-total]') || window.cart.cartTotal || null;
    }

    wrapThemeCartBuild() {
      if (!window.cart || this.isThemeCartBuildWrapped || typeof window.cart.build !== 'function') {
        return;
      }

      const originalBuild = window.cart.build.bind(window.cart);

      window.cart.build = (...args) => {
        this.syncThemeCartReferences();

        try {
          return originalBuild(...args);
        } finally {
          this.syncThemeCartReferences();
          this.syncLiveState();
        }
      };

      this.isThemeCartBuildWrapped = true;
    }

    syncThemeTotals(totalPrice) {
      if (!window.cart) return;

      window.cart.subtotal = Number(totalPrice || 0);
      if (window.theme) {
        window.theme.subtotal = window.cart.subtotal;
      }

      if (typeof window.cart.freeShippingMessageHandle === 'function') {
        window.cart.freeShippingMessageHandle(window.cart.subtotal);
      }

      if (typeof window.cart.updateProgress === 'function') {
        window.cart.updateProgress();
      }
    }

    rollbackInterruptedPreference(job) {
      if (job?.reason !== 'toggle-change') return;
      if (this.pendingJob?.reason === 'toggle-change') return;
      this.setStoredPreference(this.state.lastConfirmedPreference);
    }

    async refreshSummary(renderState = null, options = {}) {
      const [apiSummary, apiUpsell] = await this.fetchSectionFragments([
        '[data-api-cart-summary]',
        '[data-api-upsell-items]',
      ]);

      if (!apiSummary || !apiUpsell) {
        throw new Error('Cart summary render failed');
      }

      if (this.pendingJob) {
        return false;
      }

      const summaryHolder = this.getSummaryHolder();
      const upsellHolder = this.getUpsellHolder();
      const upsellWidget = this.getUpsellWidget();

      if (!summaryHolder || !upsellHolder) {
        throw new Error('Cart summary holder missing');
      }

      const nextSummaryMarkup = apiSummary.innerHTML;
      const nextUpsellMarkup = apiUpsell.innerHTML;
      summaryHolder.innerHTML = nextSummaryMarkup;
      upsellHolder.innerHTML = nextUpsellMarkup;
      const serverGiftEligibleQuantity = this.getSummaryGiftEligibleQuantity(summaryHolder);

      if (upsellWidget) {
        upsellWidget.classList.toggle('is-hidden', nextUpsellMarkup.trim() === '');
      }

      this.syncThemeCartReferences();
      if (renderState?.cart) {
        const hydratedState = this.deriveCartState(renderState.cart, {
          respectStoredPreference: options.respectStoredPreference,
          eligibleQuantityCountOverride: serverGiftEligibleQuantity,
          summaryNode: summaryHolder,
          fallbackToLineItems: false,
        });
        this.applyStateToDom(hydratedState);
      } else {
        this.syncLiveState();
      }
      return true;
    }

    refreshUpsellHolder(apiUpsell) {
      const upsellHolder = this.getUpsellHolder();
      const upsellWidget = this.getUpsellWidget();

      if (!apiUpsell || !upsellHolder) {
        throw new Error('Cart upsell render failed');
      }

      const nextMarkup = apiUpsell.innerHTML;

      upsellHolder.innerHTML = nextMarkup;

      if (upsellWidget) {
        upsellWidget.classList.toggle('is-hidden', nextMarkup.trim() === '');
      }

      return true;
    }

    async refreshUpsellHolderFromApi() {
      const apiUpsell = await this.fetchUpsellMarkup();
      return this.refreshUpsellHolder(apiUpsell);
    }

    replacePriceHolder(apiPrice) {
      if (!apiPrice) {
        throw new Error('Cart price render failed');
      }

      const priceHolder = this.getDrawer()?.querySelector('[data-cart-price-holder]');
      if (!priceHolder) {
        throw new Error('Cart price holder missing');
      }

      priceHolder.innerHTML = apiPrice.innerHTML;
      this.syncThemeCartReferences();
      return true;
    }

    async refreshPriceAndUpsellHoldersFromApi() {
      const [apiPrice, apiUpsell] = await this.fetchSectionFragments([
        '[data-api-cart-price]',
        '[data-api-upsell-items]',
      ]);

      const refreshedPriceHolder = this.replacePriceHolder(apiPrice);
      const refreshedUpsells = this.refreshUpsellHolder(apiUpsell);

      return refreshedPriceHolder && refreshedUpsells;
    }

    async refreshPriceHolder() {
      const apiPrice = await this.fetchPriceMarkup();
      return this.replacePriceHolder(apiPrice);
    }

    async mutateInsurance(quantity) {
      const response = await fetch(this.getRoute('cart_update_url', '/cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: { [this.state.variantId]: quantity },
        }),
      });

      if (!response.ok) {
        throw new Error('Shipping protection update failed');
      }

      return response.json();
    }

    scheduleReconcile(job = {}) {
      const respectStoredPreference = job.respectStoredPreference !== false;
      const modePriority = Number(job.modePriority || 0);
      const allowCoreRefreshFallback = job.allowCoreRefreshFallback !== false;
      const pendingModePriority = this.pendingJob ? Number(this.pendingJob.modePriority || 0) : -1;
      const keepPendingMode = Boolean(this.pendingJob && (job.preservePendingMode || pendingModePriority > modePriority));

      this.pendingJob = {
        reason: job.reason || this.pendingJob?.reason || 'unknown',
        refreshSummary: Boolean(this.pendingJob?.refreshSummary || job.refreshSummary),
        showErrors: Boolean(this.pendingJob?.showErrors || job.showErrors),
        respectStoredPreference: keepPendingMode
          ? this.pendingJob.respectStoredPreference !== false
          : respectStoredPreference,
        modePriority: keepPendingMode ? pendingModePriority : modePriority,
        allowCoreRefreshFallback: keepPendingMode
          ? this.pendingJob.allowCoreRefreshFallback !== false
          : allowCoreRefreshFallback,
      };

      if (this.isProcessing) return;

      window.queueMicrotask(() => this.processQueue());
    }

    scheduleResync(job = {}) {
      this.scheduleReconcile({
        reason: job.reason || this.pendingJob?.reason || 'summary-resync',
        refreshSummary: true,
        showErrors: Boolean(this.pendingJob?.showErrors || job.showErrors),
        preservePendingMode: true,
        modePriority: job.modePriority,
        allowCoreRefreshFallback: job.allowCoreRefreshFallback,
        respectStoredPreference: job.respectStoredPreference,
      });
    }

    triggerCoreCartRefresh(job = {}) {
      if (!window.cart || typeof window.cart.getCart !== 'function') {
        return false;
      }

      if (this.fallbackSignalResetTimer) {
        window.clearTimeout(this.fallbackSignalResetTimer);
        this.fallbackSignalResetTimer = 0;
      }
      this.awaitingFallbackRefresh = true;
      this.ignoringFallbackSignals = true;
      let settled = false;
      let timer = 0;

      const finish = () => {
        if (settled) return;
        settled = true;
        this.awaitingFallbackRefresh = false;
        if (timer) {
          window.clearTimeout(timer);
        }
        document.removeEventListener('theme:cart:change', onCartBuilt);
        document.removeEventListener('theme:product:added', onCartBuilt);
        this.setLoading(false);
        this.syncThemeCartReferences();
        this.syncLiveState();
        this.scheduleResync({
          reason: job.reason || 'fallback-rebuild-resync',
          showErrors: job.showErrors,
          modePriority: job.modePriority,
          allowCoreRefreshFallback: false,
          respectStoredPreference: job.respectStoredPreference,
        });
        this.fallbackSignalResetTimer = window.setTimeout(() => {
          this.ignoringFallbackSignals = false;
          this.fallbackSignalResetTimer = 0;
        }, 0);
      };

      const onCartBuilt = () => finish();

      document.addEventListener('theme:cart:change', onCartBuilt, { once: true });
      document.addEventListener('theme:product:added', onCartBuilt, { once: true });

      timer = window.setTimeout(() => finish(), 1500);
      try {
        window.cart.getCart();
      } catch (error) {
        finish();
      }
      return true;
    }

    async processQueue() {
      if (this.isProcessing) return;

      this.isProcessing = true;

      while (this.pendingJob) {
        const job = this.pendingJob;
        this.pendingJob = null;
        await this.reconcileFromCart(job);
      }

      this.isProcessing = false;
    }

    async recoverAfterFailure(job) {
      let recoveryState = null;

      try {
        const cart = await this.fetchCart();
        let cartState = this.deriveCartState(cart, {
          respectStoredPreference: false,
        });
        recoveryState = cartState;

        if (this.pendingJob) {
          this.rollbackInterruptedPreference(job);
          return;
        }

        const confirmedPreference = cartState.hasVisibleItems ? cartState.inCart : true;

        this.state.lastConfirmedPreference = confirmedPreference;
        if (job.reason === 'toggle-change') {
          this.setStoredPreference(confirmedPreference);
        }
        cartState = this.deriveCartState(cart, {
          respectStoredPreference: job.respectStoredPreference,
        });
        recoveryState = cartState;
        let usedCoreRefreshFallback = false;

        try {
          const didRefresh = await this.refreshSummary(cartState, {
            respectStoredPreference: job.respectStoredPreference,
          });
          if (!didRefresh) {
            return;
          }
          cartState = this.deriveCartState(cart, {
            respectStoredPreference: job.respectStoredPreference,
            fallbackToLineItems: false,
          });
          recoveryState = cartState;
        } catch (summaryError) {
          if (job.allowCoreRefreshFallback !== false) {
            if (this.triggerCoreCartRefresh({
              reason: 'fallback-rebuild-resync',
              showErrors: job.showErrors,
              modePriority: job.modePriority,
              allowCoreRefreshFallback: false,
              respectStoredPreference: job.respectStoredPreference,
            })) {
              usedCoreRefreshFallback = true;
            }
          } else {
            throw summaryError;
          }
        }

        if (usedCoreRefreshFallback) {
          this.setError(job.showErrors ? ERROR_MESSAGE : '');
          this.syncLiveState();
          return;
        }

        const liveCart = await this.fetchCart();
        const liveState = this.deriveCartState(liveCart, {
          respectStoredPreference: job.respectStoredPreference,
          fallbackToLineItems: false,
        });

        if (this.pendingJob || this.hasStateDrift(cartState, liveState)) {
          this.scheduleResync({
            reason: 'recovery-resync',
            showErrors: job.showErrors,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: job.allowCoreRefreshFallback,
            respectStoredPreference: job.respectStoredPreference,
          });
          return;
        }

        this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
        this.setError(job.showErrors ? ERROR_MESSAGE : '');
        this.syncThemeCartReferences();
        this.syncThemeTotals(liveState.totalPrice);
        this.applyStateToDom(liveState);
      } catch (syncError) {
        if (this.pendingJob) {
          this.rollbackInterruptedPreference(job);
          return;
        }
        const rollbackPreference = recoveryState
          ? (recoveryState.hasVisibleItems ? recoveryState.inCart : true)
          : this.state.lastConfirmedPreference;
        this.state.lastConfirmedPreference = rollbackPreference;
        if (job.reason === 'toggle-change') {
          this.setStoredPreference(rollbackPreference);
        }
        try {
          await this.refreshPriceAndUpsellHoldersFromApi();
        } catch (fragmentError) {
          // Leave the last server-rendered fragments in place if refresh is unavailable.
        }
        let didRefreshSummaryMarkup = false;

        try {
          const apiSummary = await this.fetchSummaryMarkup();
          const summaryHolder = this.getSummaryHolder();

          if (apiSummary && summaryHolder) {
            summaryHolder.innerHTML = apiSummary.innerHTML;
            didRefreshSummaryMarkup = true;
          }
        } catch (summaryFragmentError) {
          // Preserve the current promo state if summary rehydration is unavailable.
        }

        if (!didRefreshSummaryMarkup && job.allowCoreRefreshFallback !== false) {
          if (this.triggerCoreCartRefresh({
            reason: 'failure-summary-resync',
            showErrors: job.showErrors,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: false,
            respectStoredPreference: job.respectStoredPreference,
          })) {
            this.setError(job.showErrors ? ERROR_MESSAGE : '');
            this.syncLiveState();
            return;
          }
        }

        if (!didRefreshSummaryMarkup) {
          this.hideGiftPromo();
        }

        if (recoveryState?.cart) {
          recoveryState = this.deriveCartState(recoveryState.cart, {
            respectStoredPreference: job.respectStoredPreference,
            summaryNode: didRefreshSummaryMarkup ? this.getSummaryHolder() : null,
            eligibleQuantityCountOverride: didRefreshSummaryMarkup ? this.getSummaryGiftEligibleQuantity(this.getSummaryHolder()) : recoveryState.eligibleQuantityCount,
            fallbackToLineItems: false,
          });
        }
        this.setError(job.showErrors ? ERROR_MESSAGE : '');
        this.syncThemeCartReferences();
        if (recoveryState) {
          this.syncThemeTotals(recoveryState.totalPrice);
        }
        this.syncLiveState();

        if (recoveryState) {
          this.applyStateToDom({
            ...recoveryState,
            desiredEnabled: job.reason === 'toggle-change'
              ? rollbackPreference
              : recoveryState.desiredEnabled,
          }, {
            skipGiftPromo: !didRefreshSummaryMarkup,
            forceHideGiftPromo: !didRefreshSummaryMarkup,
          });
        }

        if (!didRefreshSummaryMarkup && job.reason !== 'gift-promo-recovery-resync') {
          this.scheduleResync({
            reason: 'gift-promo-recovery-resync',
            showErrors: false,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: false,
            respectStoredPreference: job.respectStoredPreference,
          });
        }

        const toggle = this.getToggle();
        if (toggle && !recoveryState) {
          toggle.checked = rollbackPreference;
        }
      } finally {
        if (!this.awaitingFallbackRefresh) {
          this.setLoading(false);
        }
      }
    }

    async reconcileFromCart(job = {}) {
      try {
        const cart = await this.fetchCart();
        let cartState = this.deriveCartState(cart, {
          respectStoredPreference: job.respectStoredPreference,
        });

        this.state.errorMessage = '';
        this.applyStateToDom(cartState, {
          skipGiftPromo: Boolean(job.refreshSummary),
        });

        if (cartState.insuranceQuantity === cartState.desiredQuantity) {
          if (job.refreshSummary) {
            const didRefresh = await this.refreshSummary(cartState, {
              respectStoredPreference: job.respectStoredPreference,
            });
            if (!didRefresh) {
              return;
            }
            cartState = this.deriveCartState(cart, {
              respectStoredPreference: job.respectStoredPreference,
              fallbackToLineItems: false,
            });
            const liveCart = await this.fetchCart();
            const liveState = this.deriveCartState(liveCart, {
              respectStoredPreference: job.respectStoredPreference,
              fallbackToLineItems: false,
            });

            if (this.pendingJob || this.hasStateDrift(cartState, liveState)) {
              this.scheduleResync({
                reason: 'no-op-resync',
                showErrors: job.showErrors,
                modePriority: job.modePriority,
                allowCoreRefreshFallback: job.allowCoreRefreshFallback,
                respectStoredPreference: job.respectStoredPreference,
              });
              return;
            }

            this.syncThemeCartReferences();
            this.syncThemeTotals(liveState.totalPrice);
            if (!this.pendingJob) {
              this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
              this.applyStateToDom(liveState);
            }
            return;
          }
          this.syncThemeCartReferences();
          if (!this.pendingJob) {
            this.state.lastConfirmedPreference = cartState.hasVisibleItems ? cartState.desiredEnabled : true;
            this.syncThemeTotals(cartState.totalPrice);
            this.applyStateToDom(cartState);
          }
          return;
        }

        this.setLoading(true);
        this.applyStateToDom(cartState);

        const updatedCart = await this.mutateInsurance(cartState.desiredQuantity);
        let confirmedState = this.deriveCartState(updatedCart, { respectStoredPreference: false });

        const didRefresh = await this.refreshSummary(confirmedState, {
          respectStoredPreference: job.respectStoredPreference,
        });
        if (!didRefresh) {
          this.setLoading(false);
          return;
        }
        confirmedState = this.deriveCartState(updatedCart, {
          respectStoredPreference: job.respectStoredPreference,
          fallbackToLineItems: false,
        });
        const liveCart = await this.fetchCart();
        const liveState = this.deriveCartState(liveCart, {
          respectStoredPreference: job.respectStoredPreference,
          fallbackToLineItems: false,
        });

        if (this.pendingJob || this.hasStateDrift(confirmedState, liveState)) {
          this.state.errorMessage = '';
          this.setLoading(false);
          this.scheduleResync({
            reason: 'mutation-resync',
            showErrors: job.showErrors,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: job.allowCoreRefreshFallback,
            respectStoredPreference: job.respectStoredPreference,
          });
          return;
        }

        this.state.errorMessage = '';
        this.setLoading(false);
        if (!this.pendingJob) {
          this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
          this.syncThemeTotals(liveState.totalPrice);
          this.applyStateToDom(liveState);
        }
      } catch (error) {
        await this.recoverAfterFailure(job);
      }
    }

    handleToggleChange(event) {
      const toggle = event.target.closest('[data-owa-shipping-protection-toggle]');
      const drawer = this.getDrawer();
      if (!toggle || !drawer || !drawer.contains(toggle)) return;

      this.setStoredPreference(Boolean(toggle.checked));
      this.scheduleReconcile({
        reason: 'toggle-change',
        modePriority: 2,
        showErrors: true,
      });
    }

    handleCartSignal(event) {
      const isFallbackSignal = event?.type === 'theme:cart:change' || event?.type === 'theme:product:added';

      if (this.ignoringFallbackSignals && isFallbackSignal) {
        return;
      }

      this.scheduleReconcile({
        reason: event?.type || 'cart-signal',
        refreshSummary: true,
        modePriority: 1,
        respectStoredPreference: true,
      });
    }

    handleDrawerLineItemFailure() {
      this.state.errorMessage = ERROR_MESSAGE;
      this.syncLiveState();

      if (window.cart && typeof window.cart.getCart === 'function') {
        this.syncThemeCartReferences();

        try {
          window.cart.getCart();
        } catch (error) {
          // Fall through to the summary reconcile below.
        }
      }

      this.scheduleReconcile({
        reason: 'drawer-line-item-failure',
        refreshSummary: true,
        showErrors: true,
        modePriority: 2,
        respectStoredPreference: true,
      });
    }

    handleDrawerRemoveClick(event) {
      const trigger = event.target.closest('[data-item-remove]');
      const item = this.getDrawerItem(trigger);

      if (!trigger || !item) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (this.isDisabledControl(trigger)) {
        return;
      }

      this.queueDrawerLineItemUpdate(
        {
          id: trigger.dataset.id || item.dataset.item || '',
          quantity: 0,
        },
        item
      ).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }

    handleDrawerQuantityButtonClick(event) {
      const trigger = event.target.closest('.cart__quantity-minus, .cart__quantity-plus');
      const item = this.getDrawerItem(trigger);

      if (!trigger || !item) {
        return;
      }

      if (this.isDisabledControl(trigger)) {
        return;
      }

      const field = this.getDrawerQuantityField(item);
      const currentQuantity = Number(field?.value || item.dataset.itemQuantity || 0);

      if (Number.isNaN(currentQuantity)) {
        return;
      }

      const nextQuantity = trigger.classList.contains('cart__quantity-minus')
        ? Math.max(currentQuantity - 1, 0)
        : currentQuantity + 1;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.queueDrawerLineItemUpdate(
        {
          id: field?.dataset?.id || item.dataset.item || '',
          quantity: nextQuantity,
        },
        item
      ).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }

    handleDrawerQuantityFieldChange(event) {
      const field = event.target.closest('.cart__quantity-field');
      const item = this.getDrawerItem(field);

      if (!field || !item) {
        return;
      }

      if (this.isDisabledControl(field)) {
        return;
      }

      const nextQuantity = Math.max(Number(field.value || 0), 0);

      if (Number.isNaN(nextQuantity)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.queueDrawerLineItemUpdate(
        {
          id: field.dataset.id || item.dataset.item || '',
          quantity: nextQuantity,
        },
        item
      ).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }

    handleDrawerQuantityUpdate(event) {
      const item = this.getDrawerItem(event.target);

      if (!item) {
        return;
      }

      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.queueDrawerLineItemUpdate(event.detail || {}, item).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }
  }

  const controller = new CartDrawerShippingProtection();
  controller.init();
})();
